import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import styles from './AuthPages.module.css';
import { brandConfig } from '../../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../../hooks/useAuthScheme';
import { appPath, termsHref, isExternalTerms } from '../../utils/navigation';
import { verifyInvitation } from '../../features/invitations/api';

interface ErrorResponse {
  error?: string;
  code?: string;
  email_resent?: boolean;
}

export default function EmailSignupPage() {
  // ライトテーマ (usodeki 等) では認証カードを明色化する
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light}` : ''}`;
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation_token');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [emailResent, setEmailResent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');

  // 招待トークンがある場合、招待情報を取得
  useEffect(() => {
    if (invitationToken) {
      fetchInvitationInfo();
    }
  }, [invitationToken]);

  const fetchInvitationInfo = async () => {
    try {
      const { ok, data } = await verifyInvitation(invitationToken!);
      if (ok && data.valid) {
        setInvitationEmail(data.email!);
        setEmail(data.email!);
        setOrganizationName(data.organization_name || '');
      }
    } catch (err) {
      console.error('Failed to fetch invitation info:', err);
    }
  };

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'パスワードは8文字以上必要です';
    if (!/[A-Z]/.test(pwd)) return '大文字を含めてください';
    if (!/[a-z]/.test(pwd)) return '小文字を含めてください';
    if (!/[0-9]/.test(pwd)) return '数字を含めてください';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorCode('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    const pwdError = validatePassword(password);
    if (pwdError) {
      setError(pwdError);
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/email/signup', {
        email,
        password,
        display_name: displayName,
        invitation_token: invitationToken || undefined,
        origin: window.location.origin,
        partner_code: brandConfig.id,
      });
      setSuccess(true);

      // GTM dataLayer イベント送信（CV計測用）
      // PII (email 等) は GA4/Google Ads へ送らないため event 名のみを積む。
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'email_signup_completed',
      });
    } catch (err: unknown) {
      const responseData = (err as { response?: { data?: ErrorResponse } })?.response?.data;
      setError(responseData?.error || 'アカウント作成に失敗しました');
      setErrorCode(responseData?.code || '');
      setEmailResent(responseData?.email_resent || false);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>

          <div className={styles.card}>
            <div className={styles.successCard}>
              <h2>確認メールを送信しました</h2>
              <p>
                {email} に確認メールを送信しました。<br />
                メール内のリンクをクリックして、アカウントを有効化してください。
              </p>
              {/* "/" は AuthGate 管轄の保護ページ。<Link> の SPA 遷移だと AuthGate が
                  再評価されず未認証のままホームが描画されるため、フルリロードで戻す */}
              <a href={appPath('/')} className={styles.backButton}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                ログインページへ戻る
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
        </div>

        <div className={styles.card}>
          <h1 className={styles.title}>
            {invitationToken && organizationName ? `${organizationName} への参加` : 'アカウント作成'}
          </h1>
          <p className={styles.subtitle}>
            {invitationToken && invitationEmail
              ? `${invitationEmail} でアカウントを作成してください`
              : '新しいアカウントを作成してください'}
          </p>

          {error && (
            (errorCode === 'EMAIL_NOT_CONFIRMED' && emailResent) ? (
              // メール未確認で自動再送信が成功した場合
              <div className={styles.success}>
                <p style={{ margin: '0 0 8px 0' }}>{error}</p>
                <p style={{ margin: 0, fontSize: '13px' }}>
                  メールが届かない場合は、迷惑メールフォルダもご確認ください。
                </p>
              </div>
            ) : (errorCode === 'EMAIL_ALREADY_CONFIRMED' || errorCode === 'EMAIL_ALREADY_REGISTERED') ? (
              // 既に登録済みのメールアドレス（確認済み）
              <div className={styles.error}>
                {errorCode === 'EMAIL_ALREADY_REGISTERED' ? (
                  // OEM (SAITE) も含め全ブランド共通の案内。ブランド別に出し分けると
                  // トラブルシューティングが難しくなるため統一する (2026-07-16 判断)
                  <>
                    <p style={{ margin: '0 0 8px 0' }}>
                      このメールアドレスはすでにLight Media関連サービスに会員登録されているため、
                      新規会員登録は不要です。
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                      すでに登録済みの方法でログイン画面からログインしてください。
                      {brandConfig.serviceName}のご利用が初めての場合、画面の案内に従って組織を作成してください。
                    </p>
                  </>
                ) : (
                  <p style={{ margin: '0 0 8px 0' }}>{error}</p>
                )}
                <p style={{ margin: 0, fontSize: '13px' }}>
                  {/* "/" (ログイン画面) は保護ページのため <Link> ではなくフルリロードで遷移する。
                      パスワード再設定の導線はログイン画面側にあるためここには置かない */}
                  <a href={appPath('/')} style={{ color: 'inherit', textDecoration: 'underline' }}>
                    ログイン画面へ
                  </a>
                </p>
              </div>
            ) : errorCode === 'EMAIL_NOT_CONFIRMED' ? (
              // メール未確認だが再送信に失敗した場合
              <div className={styles.error}>
                <p style={{ margin: '0 0 8px 0' }}>{error}</p>
                <p style={{ margin: 0, fontSize: '13px' }}>
                  メールが届かない場合は、迷惑メールフォルダもご確認ください。
                </p>
              </div>
            ) : (
              <div className={styles.error}>{error}</div>
            )
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="displayName">表示名</label>
              <input
                type="text"
                id="displayName"
                className={styles.input}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="山田 太郎"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="email">メールアドレス</label>
              <input
                type="email"
                id="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                required
                autoComplete="email"
                readOnly={!!invitationEmail}
                style={invitationEmail ? { backgroundColor: 'rgba(255,255,255,0.05)', cursor: 'not-allowed' } : undefined}
              />
              {invitationEmail && (
                <span className={styles.hint}>招待されたメールアドレスでのみ登録できます</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="password">パスワード</label>
              <input
                type="password"
                id="password"
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                required
                autoComplete="new-password"
              />
              <span className={styles.hint}>8文字以上、大文字・小文字・数字を含む</span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="confirmPassword">パスワード（確認）</label>
              <input
                type="password"
                id="confirmPassword"
                className={styles.input}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="********"
                required
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className={styles.submitButton} disabled={loading}>
              {loading ? '作成中...' : 'メールで新規登録'}
            </button>
          </form>

        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLinks}>
            <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <span className={styles.footerDivider}>|</span>
            <a href={termsHref()} {...(isExternalTerms() ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>利用規約</a>
            <span className={styles.footerDivider}>|</span>
            <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
